// recaptcha-modal.component.ts
import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RecaptchaModule } from 'ng-recaptcha';

@Component({
  selector: 'app-recaptcha-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    RecaptchaModule
  ],
  template: `
    <div class="recaptcha-modal">
      <div class="modal-header">
        <h2 mat-dialog-title>Security Verification Required</h2>
        <button mat-icon-button (click)="onCancel()" class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <div class="verification-message">
          <mat-icon class="security-icon">security</mat-icon>
          <p>For security purposes, please complete the verification below to continue with your upload.</p>
        </div>

        <div class="recaptcha-wrapper">
          <re-captcha
            [siteKey]="siteKey"
            (resolved)="onResolved($event)"
            (expired)="onExpired()"
            (errored)="onError()"
            size="normal"
          ></re-captcha>
        </div>

        <div *ngIf="error" class="error-message">
          <mat-icon>error</mat-icon>
          {{ error }}
        </div>

        <div *ngIf="isVerified && !error" class="verification-success">
          <mat-icon class="success-icon">check_circle</mat-icon>
          <span>Verified! Processing...</span>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">Cancel</button>
        <button
          *ngIf="!autoClose"
          mat-raised-button
          color="primary"
          [disabled]="!captchaToken"
          (click)="onConfirm()">
          Continue Upload
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    /* Base styles */
    .recaptcha-modal {
      width: 100%;
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 24px 16px 24px;
      position: relative;
    }

    .modal-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 500;
      flex: 1;
      padding-right: 40px;
    }

    .close-button {
      position: absolute;
      right: 8px;
      top: 8px;
    }

    .verification-message {
      text-align: center;
      margin-bottom: 24px;
    }

    .security-icon {
      font-size: 48px;
      height: 48px;
      width: 48px;
      color: #1976d2;
      margin-bottom: 16px;
    }

    .verification-message p {
      color: #666;
      margin: 0;
      line-height: 1.5;
    }

    /* reCAPTCHA wrapper - key for small screens */
    .recaptcha-wrapper {
      display: flex;
      justify-content: center;
      margin: 24px auto;
      width: 100%;
      max-width: 304px; /* reCAPTCHA default width */
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #f44336;
      font-size: 14px;
      margin-top: 16px;
      padding: 8px 12px;
      background-color: #ffebee;
      border-radius: 4px;
    }

    .error-message mat-icon {
      font-size: 20px;
      height: 20px;
      width: 20px;
    }

    .verification-success {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #4caf50;
      font-size: 14px;
      margin-top: 16px;
      padding: 12px;
      background-color: #e8f5e9;
      border-radius: 4px;
      animation: fadeIn 0.3s ease-in;
    }

    .success-icon {
      font-size: 24px;
      height: 24px;
      width: 24px;
      color: #4caf50;
    }

    @keyframes fadeIn {
      from { 
        opacity: 0; 
        transform: translateY(-10px); 
      }
      to { 
        opacity: 1; 
        transform: translateY(0); 
      }
    }

    /* Dialog overrides */
    :host ::ng-deep .mat-mdc-dialog-container {
      padding: 0;
      overflow: hidden;
    }

    :host ::ng-deep .mat-mdc-dialog-content {
      padding: 0 24px;
      margin: 0;
      max-height: calc(90vh - 120px);
      overflow-x: hidden;
      overflow-y: auto;
    }

    :host ::ng-deep .mat-mdc-dialog-actions {
      padding: 8px 16px;
      margin: 0;
      border-top: 1px solid #e0e0e0;
    }

    /* Small screens (400px and below) */
    @media screen and (max-width: 400px) {
      .modal-header {
        padding: 20px 16px 12px 16px;
      }

      .modal-header h2 {
        font-size: 18px;
      }

      :host ::ng-deep .mat-mdc-dialog-content {
        padding: 0 16px;
      }

      .security-icon {
        font-size: 40px;
        height: 40px;
        width: 40px;
      }

      .verification-message p {
        font-size: 14px;
      }

      /* Scale down reCAPTCHA for small screens */
      .recaptcha-wrapper {
        transform: scale(0.9);
        transform-origin: center;
        margin: 16px auto;
      }

      .error-message,
      .verification-success {
        font-size: 13px;
        padding: 8px 10px;
      }

      :host ::ng-deep .mat-mdc-dialog-actions {
        padding: 8px;
      }

      :host ::ng-deep .mat-mdc-dialog-actions button {
        font-size: 13px;
      }
    }

    /* Very small screens (350px and below) */
    @media screen and (max-width: 350px) {
      .modal-header {
        padding: 16px 12px 12px 12px;
      }

      .modal-header h2 {
        font-size: 16px;
      }

      :host ::ng-deep .mat-mdc-dialog-content {
        padding: 0 12px;
      }

      .recaptcha-wrapper {
        transform: scale(0.85);
      }

      /* Stack buttons vertically on very small screens */
      :host ::ng-deep .mat-mdc-dialog-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
      }

      :host ::ng-deep .mat-mdc-dialog-actions button {
        width: 100%;
        margin: 0;
      }
    }

    /* Ultra small screens (320px) */
    @media screen and (max-width: 320px) {
      .recaptcha-wrapper {
        transform: scale(0.8);
        margin: 12px auto;
      }

      .security-icon {
        font-size: 36px;
        height: 36px;
        width: 36px;
        margin-bottom: 12px;
      }

      .verification-message p {
        font-size: 13px;
      }
    }

    /* Landscape mode with limited height */
    @media screen and (max-height: 500px) {
      .security-icon {
        display: none;
      }

      .verification-message {
        margin-bottom: 16px;
      }

      .recaptcha-wrapper {
        margin: 16px auto;
      }

      :host ::ng-deep .mat-mdc-dialog-content {
        max-height: calc(100vh - 100px);
      }
    }

    /* Handle overflow for the reCAPTCHA iframe */
    :host ::ng-deep .g-recaptcha {
      max-width: 100%;
      overflow: hidden;
    }

    :host ::ng-deep .g-recaptcha > div {
      margin: 0 auto;
    }

    /* Prevent horizontal scroll on the page */
    :host ::ng-deep .cdk-overlay-pane {
      max-width: calc(100vw - 32px);
      margin: 16px;
    }

    @media screen and (max-width: 400px) {
      :host ::ng-deep .cdk-overlay-pane {
        max-width: calc(100vw - 16px);
        margin: 8px;
      }
    }
  `]
})
export class RecaptchaModalComponent {
  siteKey: string = '6LfXl2IrAAAAAIuOYXIVfT7Mi_uV1JIZS4W3NoG0'; // Use your site key
  captchaToken: string | null = null;
  error: string | null = null;
  isVerified: boolean = false;
  autoClose: boolean = true; // Enable auto-close by default
  autoCloseDelay: number = 500; // Delay in milliseconds before closing

  constructor(
    public dialogRef: MatDialogRef<RecaptchaModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Prevent dialog from closing on backdrop click during verification
    this.dialogRef.disableClose = true;

    // You can pass configuration through data if needed
    if (data?.siteKey) {
      this.siteKey = data.siteKey;
    }
    if (data?.autoClose !== undefined) {
      this.autoClose = data.autoClose;
    }
    if (data?.autoCloseDelay !== undefined) {
      this.autoCloseDelay = data.autoCloseDelay;
    }
  }

  onResolved(token: string | null): void {
    this.captchaToken = token;
    this.error = null;
    this.isVerified = true;

    // Auto-close the dialog after successful verification
    if (this.autoClose && token) {
      setTimeout(() => {
        this.dialogRef.close({ token: this.captchaToken });
      }, this.autoCloseDelay);
    }
  }

  onExpired(): void {
    this.captchaToken = null;
    this.error = 'Verification expired. Please try again.';
    this.isVerified = false;
  }

  onError(): void {
    this.captchaToken = null;
    this.error = 'Verification failed. Please try again.';
    this.isVerified = false;
  }

  onConfirm(): void {
    if (this.captchaToken) {
      this.dialogRef.close({ token: this.captchaToken });
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}